import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AuthenticatedAuthResponse,
  EnrollmentResult,
  StepUpAuthorization,
  StepUpChallenge,
  TwoFactorEmailCodeResponse,
  TwoFactorMethod,
  TwoFactorPurpose,
  TwoFactorState,
} from '@core/models';
import { suppressGlobalErrorFeedback } from '@core/interceptors/feedback-context';
import { endpoints, environment } from '@environment';

@Injectable({ providedIn: 'root' })
export class TwoFactorService {
  private readonly http = inject(HttpClient);
  private readonly root = environment.authApiUrl;

  sendLoginEmailCode(challengeToken: string): Observable<TwoFactorEmailCodeResponse> {
    return this.post(endpoints.auth.v1.twoFactorLoginEmailCode, { challengeToken });
  }

  verifyLogin(
    challengeToken: string,
    method: TwoFactorMethod,
    code: string
  ): Observable<AuthenticatedAuthResponse> {
    return this.post(endpoints.auth.v1.twoFactorLoginVerify, {
      challengeToken,
      method,
      code,
    });
  }

  cancelLogin(challengeToken: string): Observable<void> {
    return this.post(endpoints.auth.v1.twoFactorLoginCancel, { challengeToken });
  }

  startStepUp(purpose: TwoFactorPurpose): Observable<StepUpChallenge> {
    return this.post(endpoints.auth.v1.twoFactorStepUpStart, { purpose });
  }

  sendStepUpEmailCode(challengeToken: string): Observable<TwoFactorEmailCodeResponse> {
    return this.post(endpoints.auth.v1.twoFactorStepUpEmailCode, { challengeToken });
  }

  verifyStepUp(
    challengeToken: string,
    method: TwoFactorMethod,
    code: string
  ): Observable<StepUpAuthorization> {
    return this.post(endpoints.auth.v1.twoFactorStepUpVerify, {
      challengeToken,
      method,
      code,
    });
  }

  cancelStepUp(challengeToken: string): Observable<void> {
    return this.post(endpoints.auth.v1.twoFactorStepUpCancel, { challengeToken });
  }

  startEnrollment(
    mode: 'enable' | 'reconfigure',
    authorizationToken?: string
  ): Observable<EnrollmentResult> {
    return this.post(endpoints.auth.v1.twoFactorEnrollmentStart, {
      mode,
      authorizationToken,
    });
  }

  confirmEnrollment(enrollmentToken: string, code: string): Observable<TwoFactorState> {
    return this.post(endpoints.auth.v1.twoFactorEnrollmentConfirm, {
      enrollmentToken,
      code,
    });
  }

  cancelEnrollment(enrollmentToken: string): Observable<void> {
    return this.post(endpoints.auth.v1.twoFactorEnrollmentCancel, { enrollmentToken });
  }

  disable(authorizationToken: string): Observable<TwoFactorState> {
    return this.post(endpoints.auth.v1.twoFactorDisable, { authorizationToken });
  }

  private post<T>(endpoint: string, body: object): Observable<T> {
    return this.http.post<T>(`${this.root}/${endpoint}`, body, {
      context: suppressGlobalErrorFeedback(),
    });
  }
}
